/**
 * Public holidays for a few countries, as presets for special days. Pure date
 * arithmetic (no network): fixed dates plus the ones that follow Easter. The
 * restaurant decides which to close; these are only suggestions, and regional
 * holidays are not included.
 */
export const HOLIDAY_COUNTRIES = ['DE', 'AT', 'CH', 'US', 'GB'] as const;
export type HolidayCountry = (typeof HOLIDAY_COUNTRIES)[number];

export interface Holiday {
  day: string;
  name: { en: string; de: string };
}

/** Easter Sunday (Gregorian), by the anonymous algorithm. */
export function easterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const dayOfMonth = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(Date.UTC(year, month - 1, dayOfMonth));
}

const iso = (d: Date) => d.toISOString().slice(0, 10);
const plus = (d: Date, n: number) => new Date(d.getTime() + n * 86400_000);
const fixed = (y: number, m: number, d: number) => `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

/** The nth weekday (1 = Monday ... 7 = Sunday) of a month; n = -1 for the last. */
function nthWeekday(y: number, m: number, weekday: number, n: number): string {
  if (n > 0) {
    const first = new Date(Date.UTC(y, m - 1, 1));
    const offset = (weekday - (((first.getUTCDay() + 6) % 7) + 1) + 7) % 7;
    return iso(plus(first, offset + (n - 1) * 7));
  }
  const last = new Date(Date.UTC(y, m, 0));
  const back = ((((last.getUTCDay() + 6) % 7) + 1) - weekday + 7) % 7;
  return iso(plus(last, -back));
}

export function holidaysFor(country: HolidayCountry, year: number): Holiday[] {
  const e = easterSunday(year);
  const out: Holiday[] = [];
  const h = (day: string, en: string, de: string) => out.push({ day, name: { en, de } });
  const newYear = () => h(fixed(year, 1, 1), "New Year's Day", 'Neujahr');
  const labour = () => h(fixed(year, 5, 1), 'Labour Day', 'Tag der Arbeit');
  const goodFriday = () => h(iso(plus(e, -2)), 'Good Friday', 'Karfreitag');
  const easterMonday = () => h(iso(plus(e, 1)), 'Easter Monday', 'Ostermontag');
  const ascension = () => h(iso(plus(e, 39)), 'Ascension Day', 'Christi Himmelfahrt');
  const whitMonday = () => h(iso(plus(e, 50)), 'Whit Monday', 'Pfingstmontag');
  const christmas = () => {
    h(fixed(year, 12, 25), 'Christmas Day', '1. Weihnachtstag');
    h(fixed(year, 12, 26), 'Boxing Day / Second Christmas Day', '2. Weihnachtstag');
  };

  if (country === 'DE') {
    newYear(); goodFriday(); easterMonday(); labour(); ascension(); whitMonday();
    h(fixed(year, 10, 3), 'German Unity Day', 'Tag der Deutschen Einheit');
    christmas();
  } else if (country === 'AT') {
    newYear(); h(fixed(year, 1, 6), 'Epiphany', 'Heilige Drei Könige'); easterMonday(); h(fixed(year, 5, 1), 'Labour Day', 'Staatsfeiertag');
    ascension(); whitMonday(); h(iso(plus(e, 60)), 'Corpus Christi', 'Fronleichnam');
    h(fixed(year, 8, 15), 'Assumption Day', 'Mariä Himmelfahrt'); h(fixed(year, 10, 26), 'National Day', 'Nationalfeiertag');
    h(fixed(year, 11, 1), "All Saints' Day", 'Allerheiligen'); h(fixed(year, 12, 8), 'Immaculate Conception', 'Mariä Empfängnis');
    christmas();
  } else if (country === 'CH') {
    newYear(); goodFriday(); easterMonday(); ascension(); whitMonday();
    h(fixed(year, 8, 1), 'Swiss National Day', 'Bundesfeier');
    christmas();
  } else if (country === 'US') {
    newYear();
    h(nthWeekday(year, 1, 1, 3), 'Martin Luther King Jr. Day', 'Martin-Luther-King-Tag');
    h(nthWeekday(year, 2, 1, 3), "Presidents' Day", 'Presidents Day');
    h(nthWeekday(year, 5, 1, -1), 'Memorial Day', 'Memorial Day');
    h(fixed(year, 6, 19), 'Juneteenth', 'Juneteenth');
    h(fixed(year, 7, 4), 'Independence Day', 'Unabhängigkeitstag');
    h(nthWeekday(year, 9, 1, 1), 'Labor Day', 'Labor Day');
    h(nthWeekday(year, 10, 1, 2), 'Columbus Day', 'Columbus Day');
    h(fixed(year, 11, 11), 'Veterans Day', 'Veterans Day');
    h(nthWeekday(year, 11, 4, 4), 'Thanksgiving', 'Thanksgiving');
    h(fixed(year, 12, 25), 'Christmas Day', 'Weihnachten');
  } else {
    newYear(); goodFriday(); easterMonday();
    h(nthWeekday(year, 5, 1, 1), 'Early May bank holiday', 'Early May Bank Holiday');
    h(nthWeekday(year, 5, 1, -1), 'Spring bank holiday', 'Spring Bank Holiday');
    h(nthWeekday(year, 8, 1, -1), 'Summer bank holiday', 'Summer Bank Holiday');
    h(fixed(year, 12, 25), 'Christmas Day', '1. Weihnachtstag');
    h(fixed(year, 12, 26), 'Boxing Day', '2. Weihnachtstag');
  }
  return out.sort((a, b) => a.day.localeCompare(b.day));
}
